import React, { useEffect, useRef, useState } from 'react';
import trainingService, { TrainingComment } from '../services/trainingService';
import { linkify } from '../utils/linkify';

interface Props {
    entryId: string;
    entryTitle: string;
    currentUserId: string;
    onClose: () => void;
    /** Lets the page drop the comment from its inline preview too. */
    onDeleted: (commentId: string) => void;
}

const TrainingCommentModal: React.FC<Props> = ({
    entryId,
    entryTitle,
    currentUserId,
    onClose,
    onDeleted,
}) => {
    const [comments, setComments] = useState<TrainingComment[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    const fetchComments = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            const data = await trainingService.getComments(entryId);
            setComments(data);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleDelete = async (commentId: string) => {
        if (!window.confirm('Delete this message?')) return;
        try {
            await trainingService.deleteComment(commentId);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
            onDeleted(commentId);
        } catch {
            // silently fail
        }
    };

    useEffect(() => {
        fetchComments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [comments]);

    const formatTime = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: '85vh' }}>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-cyan-500 to-blue-500">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <i className="fa-solid fa-comments text-white text-sm"></i>
                        </div>
                        <div>
                            <p className="text-xs text-white/70 font-medium uppercase tracking-wider">Conversation</p>
                            <h3 className="text-white font-bold text-sm leading-tight truncate max-w-[240px]">{entryTitle}</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => fetchComments(true)}
                            disabled={refreshing}
                            title="Refresh"
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors disabled:opacity-50"
                        >
                            <i className={`fa-solid fa-rotate-right text-sm ${refreshing ? 'animate-spin' : ''}`}></i>
                        </button>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                </div>

                {/* Thread — read only */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-slate-400">
                            <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
                            <i className="fa-regular fa-comment-dots text-3xl"></i>
                            <p className="text-sm font-medium">No messages yet</p>
                            <p className="text-xs">Comments will appear here once sent</p>
                        </div>
                    ) : (
                        comments.map((c) => {
                            const isOwn = c.userId === currentUserId;
                            return (
                                <div key={c.id} className={`flex flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
                                    <span className="text-[11px] text-slate-400 px-1">
                                        {isOwn ? 'You' : c.senderName}
                                    </span>
                                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                                        isOwn
                                            ? 'bg-cyan-500 text-white rounded-tr-sm'
                                            : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm'
                                    }`}>
                                        {linkify(c.message)}
                                    </div>
                                    <span className="text-[10px] text-slate-400 px-1 flex items-center gap-2">
                                        {formatTime(c.createdAt)}
                                        {isOwn && (
                                            <button
                                                onClick={() => handleDelete(c.id)}
                                                title="Delete message"
                                                className="text-slate-400 hover:text-red-500 transition-colors"
                                            >
                                                <i className="fa-solid fa-trash-can"></i>
                                            </button>
                                        )}
                                    </span>
                                </div>
                            );
                        })
                    )}
                    <div ref={bottomRef} />
                </div>
            </div>
        </div>
    );
};

export default TrainingCommentModal;
